import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Check, Undo2, Gift, Plus, CalendarPlus, ChevronLeft, ChevronRight, Pencil, Trash2, FileText, Link2, Copy, MessageCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t, formatDate, formatTime, formatMoney } from '../i18n';
import { STATUS_TONE } from '../lib/appointments';
import { dayKey, instantAt, monthKey, addMonthsToKey, monthRange, dayHasPassed } from '../lib/practiceTime';
import { collected, collectedForSessions } from '../lib/payments';
import { OWING_STATUSES, lacking } from '../lib/billing';
import { callServer } from '../lib/serverCall';
import {
  Card, Button, Select, Input, Field, Badge, Alert, Spinner, EmptyState, Modal, Table,
} from '../components/ui';

/**
 * Per-session payment tracking.
 *
 * Two payors, tracked independently: the patient's copay or private fee, and
 * the obra social's reimbursement. They arrive at different times, so each has
 * its own status and date.
 *
 * A payment is money (0072). It goes to the oldest debt first — what happened,
 * then what is booked — until it runs out, so a session can be part paid; what
 * is left stays as credit that later bookings draw on. It used to be a number
 * of sessions, whatever they cost, which is how $70.000 once settled $80.000.
 */

/**
 * A typed payment date as the instant stored for it: 12:00 that day on the
 * practice's clock. new Date("2026-09-14") is midnight UTC, 21:00 on the 13th
 * in Buenos Aires, so every payment showed the day before (fixed in 0048).
 */
const paidAtInstant = (key) => instantAt(key, '12:00').toISOString();

const MONEY_STATUS_TONE = {
  pending: 'warning',
  partial: 'warning',
  paid: 'success',
  waived: 'neutral',
  not_applicable: 'neutral',
};

const PaymentsView = () => {
  const { therapistId } = useSession();
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [patientFilter, setPatientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [invoiceOnly, setInvoiceOnly] = useState(false);
  // A YYYY-MM month on the practice's clock (lib/practiceTime.js).
  const [month, setMonth] = useState(() => monthKey());
  const [outstanding, setOutstanding] = useState([]);
  const [payments, setPayments] = useState([]);
  // The obra social's money, by the date it arrived (0070).
  const [planPaid, setPlanPaid] = useState([]);
  const [editingPayment, setEditingPayment] = useState(null);

  const [paying, setPaying] = useState(null);
  const [payment, setPayment] = useState({ amount: '', method: 'transfer', paidAt: '', notes: '' });
  // Links de pago (0074): whether she takes Mercado Pago, and the link being made.
  const [takesMercadoPago, setTakesMercadoPago] = useState(false);
  const [linking, setLinking] = useState(null);
  const [linkAmount, setLinkAmount] = useState('');
  const [link, setLink] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [copied, setCopied] = useState(false);

  const SELECT = `
    id, starts_at, status, is_billable, billing_mode,
    patient_amount, patient_paid_amount, patient_payment_status, patient_paid_at,
    plan_amount, plan_payment_status, plan_paid_at, plan_payment_due_on,
    dependents ( id, first_name, last_name ),
    treatments ( needs_invoice, insurance_plans ( name, insurance_companies ( name ) ) ),
    therapists ( first_name, last_name )
  `;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start: from, end: to } = monthRange(month);

      // The month's sessions, for reviewing a period.
      let monthQuery = supabase
        .from('appointments').select(SELECT)
        .gte('starts_at', from)
        .lt('starts_at', to)
        .order('starts_at', { ascending: false });

      // Everything still owed, of any date. Debt accumulates rather than
      // belonging to a month, so chasing it must not be filtered by one:
      // a session unpaid since March is exactly what you want to see in
      // September.
      let debtQuery = supabase
        .from('appointments').select(SELECT)
        .in('patient_payment_status', OWING_STATUSES)
        .eq('is_billable', true)
        .in('status', ['completed', 'cancelled', 'no_show'])
        .order('starts_at');

      // Always the viewer's own practice, admin included. "Qué te pagó cada
      // paciente" is about this professional's money; being admin used to widen
      // it to every professional's patients, which put another professional's
      // patients and what they owe in front of whoever happened to be admin.
      // Practice-wide figures belong in Flujo de caja, not here.
      monthQuery = monthQuery.eq('therapist_id', therapistId);
      debtQuery = debtQuery.eq('therapist_id', therapistId);

      const [monthResult, debtResult, patientResult, balanceResult, paymentResult, planResult] = await Promise.all([
        monthQuery,
        debtQuery,
        // Current and former patients alike: a debt outlives the discharge.
        supabase
          .from('treatments')
          .select('dependents ( id, first_name, last_name )')
          .eq('therapist_id', therapistId)
          .in('status', ['approved', 'revoked']),
        supabase
          .from('patient_payment_balances')
          .select('id, dependent_id, amount, paid_at, remaining_amount')
          .eq('therapist_id', therapistId)
          .gt('remaining_amount', 0),
        supabase
          .from('patient_payment_balances')
          .select('id, dependent_id, amount, paid_at, allocated, remaining_amount, method')
          .eq('therapist_id', therapistId)
          .gte('paid_at', from)
          .lt('paid_at', to)
          .order('paid_at', { ascending: false }),
        // What obras sociales paid this month, whatever month the sessions they
        // pay for belong to. There is no other record of that money: the
        // session marked paid, on the date it was marked, is it.
        supabase
          .from('appointments')
          .select('id, dependent_id, plan_amount, plan_paid_at')
          .eq('therapist_id', therapistId)
          .eq('plan_payment_status', 'paid')
          .gte('plan_paid_at', from)
          .lt('plan_paid_at', to),
      ]);

      if (monthResult.error) throw monthResult.error;
      setRows(monthResult.data ?? []);
      setOutstanding(debtResult.data ?? []);
      setPatients((patientResult.data ?? [])
        .map((row) => row.dependents)
        .filter(Boolean)
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'es')));
      setBalances(balanceResult.data ?? []);
      setPayments(paymentResult.data ?? []);
      setPlanPaid(planResult.data ?? []);
    } catch (loadError) {
      setError(loadError.message ?? String(loadError));
    } finally {
      setLoading(false);
    }
  }, [therapistId, month]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    supabase.rpc('my_mercadopago').then(({ data }) => setTakesMercadoPago(Boolean(data?.connected)));
  }, []);

  const openLink = (debtor) => {
    setLinking(debtor);
    setLinkAmount(String(Math.round(debtor.total)));
    setLink(null);
    setLinkError(null);
    setCopied(false);
  };

  const createLink = async () => {
    setBusyId('link');
    setLinkError(null);
    try {
      setLink(await callServer('mercadopago-link', {
        dependent_id: linking.id,
        therapist_id: therapistId,
        amount: Number(linkAmount) || 0,
      }));
    } catch (linkFailure) {
      setLinkError(linkFailure.message);
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const visible = useMemo(() => rows.filter((row) => {
    if (patientFilter && row.dependents?.id !== patientFilter) return false;
    if (statusFilter === 'pending') {
      return row.is_billable && (
        OWING_STATUSES.includes(row.patient_payment_status) ||
        row.plan_payment_status === 'pending'
      );
    }
    if (statusFilter === 'paid') {
      return row.patient_payment_status === 'paid';
    }
    return true;
  }), [rows, patientFilter, statusFilter]);

  // Who owes what, rolled up per patient across every unpaid session. This is
  // the view for chasing money: one line per person, not one per session.
  const debtors = useMemo(() => {
    const map = new Map();
    for (const row of outstanding) {
      const patient = row.dependents;
      if (!patient) continue;
      if (!map.has(patient.id)) {
        map.set(patient.id, {
          id: patient.id,
          name: `${patient.first_name} ${patient.last_name}`,
          coverage: row.treatments?.insurance_plans?.insurance_companies?.name ?? null,
          sessions: 0, total: 0, oldest: row.starts_at,
        });
      }
      const entry = map.get(patient.id);
      entry.sessions += 1;
      // What it lacks, not its price: part of it may be paid (0072).
      entry.total += lacking(row);
      if (new Date(row.starts_at) < new Date(entry.oldest)) entry.oldest = row.starts_at;
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [outstanding]);

  const totalOwed = useMemo(
    () => debtors.reduce((sum, d) => sum + d.total, 0),
    [debtors]
  );

  const shiftMonth = (offset) => setMonth((current) => addMonthsToKey(current, offset));

  // Totals follow the patient filter but deliberately ignore the status
  // filter: showing "Cobrado $0" while filtered to unpaid sessions would
  // contradict the table underneath it.
  const totalsScope = useMemo(
    () => (patientFilter ? rows.filter((row) => row.dependents?.id === patientFilter) : rows),
    [rows, patientFilter]
  );

  // Two figures, because they answer different questions and were both
  // called "Cobrado" (0070). The money that came in this month is what the
  // professional has: payments as registered, obra social included, whatever
  // month the sessions belong to — and a payment made before there is a
  // session to put it against is already money. What this month's sessions
  // collected is the other one, and it is the price list, not the cash.
  const money = useMemo(() => collected(
    patientFilter ? payments.filter((row) => row.dependent_id === patientFilter) : payments,
    patientFilter ? planPaid.filter((row) => row.dependent_id === patientFilter) : planPaid,
  ), [payments, planPaid, patientFilter]);

  const forSessions = useMemo(() => collectedForSessions(totalsScope), [totalsScope]);

  const totals = useMemo(() => totalsScope.reduce((acc, row) => {
    if (row.is_billable && OWING_STATUSES.includes(row.patient_payment_status)
        && ['completed', 'cancelled', 'no_show'].includes(row.status)) {
      acc.owed += lacking(row);
    }
    if (row.plan_payment_status === 'pending' && row.is_billable
        && ['completed', 'cancelled', 'no_show'].includes(row.status)) {
      acc.planOwed += Number(row.plan_amount ?? 0);
      // Overdue only once the agreed term has passed. Lumping the two together
      // made the figure useless: most of it is simply not due yet.
      if (row.plan_payment_due_on && dayHasPassed(row.plan_payment_due_on)) {
        acc.planOverdue += Number(row.plan_amount ?? 0);
      }
    }
    return acc;
  }, { owed: 0, planOwed: 0, planOverdue: 0 }), [totalsScope]);

  const togglePlan = async (row, paid) => {
    setBusyId(row.id);
    const { error: planError } = await supabase.rpc('set_plan_payment', {
      p_appointment_id: row.id,
      p_paid: paid,
    });
    setBusyId(null);
    if (planError) { setError(planError.message); return; }
    load();
  };

  // Bonificado (0072): given without charge. Whatever money had reached the
  // session goes to the next debt, or waits as credit. It replaces "Deshacer el
  // cobro": once money flows to the oldest debt by itself, taking it off one
  // session would only send it straight back.
  const setWaived = async (row, waived) => {
    setBusyId(row.id);
    const { error: waiveError } = await supabase.rpc('set_session_waived', {
      p_appointment_id: row.id,
      p_waived: waived,
    });
    setBusyId(null);
    if (waiveError) { setError(waiveError.message); return; }
    load();
  };

  const openEdit = (row, patient) => {
    setEditingPayment({
      id: row.id,
      name: patient ? patient.first_name + ' ' + patient.last_name : '',
      amount: String(row.amount),
      // The payment's day on the practice's clock, not the UTC date of the instant.
      paidAt: dayKey(row.paid_at),
      method: row.method ?? 'transfer',
    });
  };

  const saveEdit = async () => {
    setBusyId('edit');
    // A corrected payment is placed again against the oldest debt (0072).
    const { error: editError } = await supabase.rpc('correct_patient_payment', {
      p_payment_id: editingPayment.id,
      p_amount: Number(editingPayment.amount) || 0,
      p_paid_at: paidAtInstant(editingPayment.paidAt),
      p_method: editingPayment.method || null,
      p_notes: null,
    });
    setBusyId(null);
    if (editError) { setError(editError.message); return; }
    setEditingPayment(null);
    load();
  };

  // Voided, not deleted: the sessions it settled go back to owing, and the
  // payment stays on record with the reason (migration 0037).
  const voidPayment = async (row, patient) => {
    const who = patient ? patient.first_name + ' ' + patient.last_name : '';
    const reason = window.prompt(t('payments.voidReasonPrompt', { name: who }));
    if (reason === null) return;
    if (!reason.trim()) { setError(t('payments.voidReasonRequired')); return; }
    setBusyId(row.id);
    const { error: voidError } = await supabase.rpc('void_patient_payment', {
      p_payment_id: row.id,
      p_reason: reason.trim(),
    });
    setBusyId(null);
    if (voidError) { setError(voidError.message); return; }
    load();
  };

  const openPayment = (dependent, suggestedAmount) => {
    setPaying(dependent);
    setPayment({
      amount: suggestedAmount ? String(suggestedAmount) : '',
      method: 'transfer',
      paidAt: dayKey(),
      notes: '',
    });
  };

  const submitPayment = async () => {
    setBusyId('payment');
    const { data, error: payError } = await supabase.rpc('record_patient_payment', {
      p_dependent_id: paying.id,
      p_amount: Number(payment.amount) || 0,
      p_paid_at: paidAtInstant(payment.paidAt || dayKey()),
      p_method: payment.method || null,
      p_notes: payment.notes || null,
    });
    setBusyId(null);
    if (payError) { setError(payError.message); return; }
    setPaying(null);
    // What the money reached, as the database placed it.
    setNotice({ reached: data.reached ?? [], credit: Number(data.credit ?? 0) });
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('payments.title')}</h1>
          <p className="text-sm text-slate-500">{t('payments.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* What to bill each obra social and each invoiced patient, by month (0053). */}
          <Link to="/pagos/facturacion">
            <Button variant="secondary"><FileText size={16} />{t('billingSummary.open')}</Button>
          </Link>
          <Link to="/reservar">
            <Button variant="secondary"><CalendarPlus size={16} />{t('booking.title')}</Button>
          </Link>
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {notice && (
        <Alert tone="success" title={t('payments.registered')}>
          {notice.reached.length === 0
            ? t('payments.reachedNone')
            : t('payments.reachedList', {
              list: notice.reached.map((item) => t(
                item.status === 'partial' ? 'payments.reachedItemPartial' : 'payments.reachedItem',
                {
                  day: formatDate(item.starts_at, { day: 'numeric', month: 'short' }),
                  amount: formatMoney(item.amount),
                  price: formatMoney(item.price),
                },
              )).join(' · '),
            })}
          {notice.credit > 0 && ` ${t('payments.creditMoney', { amount: formatMoney(notice.credit) })}`}
        </Alert>
      )}

      {/* Debt is cumulative, so this panel deliberately ignores the month
          selector below: an unpaid March session still needs chasing in
          September. */}
      {debtors.length > 0 && (
        <Card
          title={t('payments.whoOwesTitle')}
          subtitle={t('payments.whoOwesSubtitle', { amount: formatMoney(totalOwed) })}
        >
          <Table headers={[
            t('appointments.patient'),
            t('payments.unpaidSessions'),
            t('payments.oldestUnpaid'), t('payments.totalOwed'), '',
          ]}>
            {debtors.map((debtor) => (
              <tr key={debtor.id}>
                <td className="px-3 py-2">
                  <p className="font-medium text-slate-900">{debtor.name}</p>
                  <p className="text-xs text-slate-500">{debtor.coverage ?? t('insurance.particular')}</p>
                </td>
                <td className="px-3 py-2 text-slate-700">{debtor.sessions}</td>
                <td className="px-3 py-2 text-slate-700">
                  {formatDate(debtor.oldest)}
                  <p className="text-xs text-slate-400">
                    {t('payments.daysAgo', {
                      count: Math.floor((Date.now() - new Date(debtor.oldest)) / 86400000),
                    })}
                  </p>
                </td>
                <td className="px-3 py-2 font-semibold text-rose-700">{formatMoney(debtor.total)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex flex-wrap justify-end gap-2">
                    {takesMercadoPago && (
                      <Button variant="secondary" onClick={() => openLink(debtor)}>
                        <Link2 size={14} />{t('mercadopago.linkButton')}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => openPayment(
                        patients.find((p) => p.id === debtor.id) ?? { id: debtor.id, first_name: debtor.name, last_name: '' },
                        debtor.total
                      )}
                    >
                      <Wallet size={14} />{t('payments.registerPayment')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {/* Registered payments, so a wrong amount can be corrected. Voiding one
          takes its money back from every session it reached — a credit must
          never outlive the payment that created it. */}
      {payments.length > 0 && (
        <Card title={t('payments.registeredTitle')} subtitle={t('payments.registeredSubtitle')}>
          <Table headers={[
            t('appointments.patient'), t('payments.paidAt'), t('payments.amount'),
            t('payments.allocated'), t('payments.inCredit'), t('payments.method'), '',
          ]}>
            {payments.map((row) => {
              const patient = patients.find((p) => p.id === row.dependent_id);
              return (
                <tr key={row.id}>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {patient ? `${patient.first_name} ${patient.last_name}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{formatDate(row.paid_at)}</td>
                  <td className="px-3 py-2 font-medium">{formatMoney(row.amount)}</td>
                  {/* How much of it reached sessions, and how much is waiting
                      for one (0072). */}
                  <td className="px-3 py-2 text-slate-700">{formatMoney(row.allocated)}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {Number(row.remaining_amount) > 0 ? formatMoney(row.remaining_amount) : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {row.method ? t(`payments.methods.${row.method}`) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" onClick={() => openEdit(row, patient)} title={t('common.edit')}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" onClick={() => voidPayment(row, patient)} title={t('payments.voidPayment')}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </Table>
        </Card>
      )}

      {balances.length > 0 && (
        <Alert tone="info" title={t('payments.creditsTitle')}>
          <ul className="mt-1 space-y-0.5">
            {balances.map((balance) => {
              const patient = patients.find((p) => p.id === balance.dependent_id);
              return (
                <li key={balance.id}>
                  {patient ? `${patient.first_name} ${patient.last_name}` : '—'} ·{' '}
                  {t('payments.creditLine', {
                    amount: formatMoney(balance.remaining_amount),
                    paid: formatMoney(balance.amount),
                    day: formatDate(balance.paid_at, { day: 'numeric', month: 'short' }),
                  })}
                </li>
              );
            })}
          </ul>
        </Alert>
      )}

      {/* The month selector scopes the session table below, not the debt panel
          above — reviewing a period and chasing arrears are different jobs. */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="secondary" onClick={() => shiftMonth(-1)}><ChevronLeft size={16} /></Button>
        <div className="text-center">
          <p className="text-sm font-semibold text-slate-800 first-letter:uppercase">
            {formatDate(`${month}-01`, { month: 'long', year: 'numeric' })}
          </p>
          <p className="text-xs text-slate-500">{t('payments.monthScope')}</p>
        </div>
        <Button variant="secondary" onClick={() => shiftMonth(1)}><ChevronRight size={16} /></Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card><p className="text-xs uppercase text-slate-500">{t('payments.collectedThisMonth')}</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700">{formatMoney(money.total)}</p>
          <p className="mt-1 text-xs text-slate-500">
            {t('payments.collectedSplit', {
              patient: formatMoney(money.fromPatients),
              plan: formatMoney(money.fromPlans),
            })}
          </p>
          {money.unassigned > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              {t('payments.collectedUnassigned', { amount: formatMoney(money.unassigned) })}
            </p>
          )}
        </Card>
        <Card><p className="text-xs uppercase text-slate-500">{t('payments.collectedForSessions')}</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{formatMoney(forSessions)}</p>
          <p className="mt-1 text-xs text-slate-500">{t('payments.collectedForSessionsHint')}</p>
          {/* The two figures answer different questions and routinely differ,
              which read as an error to the first person who saw them side by
              side (23 Sep 2026). Said out loud, only when they do differ. */}
          {Math.abs(forSessions - money.total) >= 1 && (
            <p className="mt-1 text-xs text-slate-400">{t('payments.collectedDiffers')}</p>
          )}
        </Card>
        <Card><p className="text-xs uppercase text-slate-500">{t('payments.patientOwes')}</p>
          <p className="mt-1 text-2xl font-bold text-rose-700">{formatMoney(totals.owed)}</p></Card>
        <Card><p className="text-xs uppercase text-slate-500">{t('payments.planOwes')}</p>
          <p className="mt-1 text-2xl font-bold text-amber-700">{formatMoney(totals.planOwed)}</p>
          {totals.planOverdue > 0 ? (
            <p className="mt-1 text-xs font-medium text-rose-700">
              {t('payments.planOverdue', { amount: formatMoney(totals.planOverdue) })}
            </p>
          ) : totals.planOwed > 0 && (
            <p className="mt-1 text-xs text-slate-500">{t('payments.planAllOnTime')}</p>
          )}
        </Card>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Field label={t('appointments.patient')}>
            <Select value={patientFilter} onChange={(event) => setPatientFilter(event.target.value)} className="min-w-48">
              <option value="">{t('common.all')}</option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>{patient.first_name} {patient.last_name}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.status')}>
            <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="min-w-40">
              <option value="pending">{t('payments.filterPending')}</option>
              <option value="paid">{t('payments.filterPaid')}</option>
              <option value="all">{t('common.all')}</option>
            </Select>
          </Field>
          {/* So a professional who invoices only some patients can list exactly
              the sessions she has to issue one for. */}
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-600">
            <input type="checkbox" checked={invoiceOnly} onChange={(event) => setInvoiceOnly(event.target.checked)} />
            {t('payments.invoiceOnly')}
          </label>
          {patientFilter && (
            <Button onClick={() => openPayment(patients.find((p) => p.id === patientFilter))}>
              <Plus size={16} />{t('payments.registerPayment')}
            </Button>
          )}
        </div>

        {(invoiceOnly ? visible.filter((row) => row.treatments?.needs_invoice) : visible).length === 0 ? (
          <EmptyState icon={Wallet} title={t('payments.nothingToShow')} />
        ) : (
          <Table headers={[
            t('appointments.patient'), t('payments.sessionDate'), t('common.status'),
            t('payments.patientPayment'), t('payments.patientPaymentDate'),
            t('payments.planPayment'), t('payments.planPaymentDate'), '',
          ]}>
            {(invoiceOnly ? visible.filter((row) => row.treatments?.needs_invoice) : visible).map((row) => (
              <tr key={row.id} className={!row.is_billable ? 'opacity-50' : undefined}>
                <td className="px-3 py-2">
                  <p className="font-medium text-slate-900">
                    {row.dependents?.first_name} {row.dependents?.last_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t(`insurance.billingModes.${row.billing_mode}`)}
                    {row.treatments?.insurance_plans
                      && ` · ${row.treatments.insurance_plans.insurance_companies?.name}`}
                    {row.treatments?.needs_invoice && (
                      <Badge tone="info" className="ml-1">{t('payments.invoiceBadge')}</Badge>
                    )}
                  </p>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                  {formatDate(row.starts_at)}
                  <span className="ml-1 text-xs text-slate-500">{formatTime(row.starts_at)}</span>
                </td>
                <td className="px-3 py-2">
                  <Badge tone={STATUS_TONE[row.status]}>{t(`appointments.status.${row.status}`)}</Badge>
                  {!row.is_billable && <p className="mt-1 text-xs text-slate-400">{t('payments.notBillable')}</p>}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <span className="font-medium">{formatMoney(row.patient_amount)}</span>
                  <Badge tone={MONEY_STATUS_TONE[row.patient_payment_status]} className="ml-2">
                    {t(`payments.status.${row.patient_payment_status}`)}
                  </Badge>
                  {/* A part-paid session says what it still lacks (0072). */}
                  {row.patient_payment_status === 'partial' && (
                    <p className="mt-1 text-xs text-amber-700">
                      {t('payments.partialLeft', { amount: formatMoney(lacking(row)) })}
                    </p>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                  {row.patient_paid_at ? formatDate(row.patient_paid_at) : '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {row.billing_mode === 'plan' ? (
                    <>
                      <span className="font-medium">{formatMoney(row.plan_amount)}</span>
                      <Badge tone={MONEY_STATUS_TONE[row.plan_payment_status]} className="ml-2">
                        {t(`payments.status.${row.plan_payment_status}`)}
                      </Badge>
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                  {row.plan_paid_at ? formatDate(row.plan_paid_at) : row.plan_payment_due_on ? (
                    <>
                      <span className={dayHasPassed(row.plan_payment_due_on)
                        ? 'font-medium text-rose-700' : 'text-slate-500'}>
                        {formatDate(row.plan_payment_due_on)}
                      </span>
                      <p className="text-xs text-slate-400">
                        {dayHasPassed(row.plan_payment_due_on)
                          ? t('payments.overdue') : t('payments.expected')}
                      </p>
                    </>
                  ) : '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    {OWING_STATUSES.includes(row.patient_payment_status) && row.is_billable && (
                      <>
                        <Button
                          variant="ghost"
                          loading={busyId === row.id}
                          onClick={() => openPayment(row.dependents, lacking(row))}
                          title={t('payments.registerPayment')}
                        >
                          <Wallet size={14} />
                        </Button>
                        <Button variant="ghost" loading={busyId === row.id} onClick={() => setWaived(row, true)}
                          title={t('payments.waive')}>
                          <Gift size={14} />
                        </Button>
                      </>
                    )}
                    {row.patient_payment_status === 'waived' && (
                      <Button variant="ghost" loading={busyId === row.id} onClick={() => setWaived(row, false)}
                        title={t('payments.unwaive')}>
                        <Undo2 size={14} />
                      </Button>
                    )}
                    {row.billing_mode === 'plan' && row.plan_payment_status === 'pending' && (
                      <Button variant="ghost" loading={busyId === row.id} onClick={() => togglePlan(row, true)}
                        title={t('payments.markPlanPaid')}>
                        <Check size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal
        open={Boolean(editingPayment)}
        title={t('payments.editPayment')}
        onClose={() => setEditingPayment(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditingPayment(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveEdit} loading={busyId === 'edit'}>{t('common.save')}</Button>
          </>
        }
      >
        {editingPayment && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{editingPayment.name}</p>
            <Alert tone="info">{t('payments.correctExplanation')}</Alert>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('payments.amount')} required>
                <Input type="number" min="0" value={editingPayment.amount}
                  onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })} />
              </Field>
              <Field label={t('payments.paidAt')}>
                <Input type="date" value={editingPayment.paidAt}
                  onChange={(e) => setEditingPayment({ ...editingPayment, paidAt: e.target.value })} />
              </Field>
              <Field label={t('payments.method')}>
                <Select value={editingPayment.method}
                  onChange={(e) => setEditingPayment({ ...editingPayment, method: e.target.value })}>
                  {['cash', 'transfer', 'card', 'other'].map((m) => (
                    <option key={m} value={m}>{t(`payments.methods.${m}`)}</option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(linking)}
        title={t('mercadopago.linkTitle')}
        onClose={() => setLinking(null)}
        footer={link ? (
          <Button variant="secondary" onClick={() => setLinking(null)}>{t('common.close')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setLinking(null)}>{t('common.cancel')}</Button>
            <Button onClick={createLink} loading={busyId === 'link'}>
              <Link2 size={16} />{t('mercadopago.createLink')}
            </Button>
          </>
        )}
      >
        {linking && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{linking.name}</p>
            {linkError && <Alert tone="danger">{linkError}</Alert>}
            {!link ? (
              <>
                <Field label={t('mercadopago.linkAmount')} required>
                  <Input type="number" min="1" value={linkAmount}
                    onChange={(event) => setLinkAmount(event.target.value)} />
                </Field>
                <Alert tone="info">{t('mercadopago.linkHint')}</Alert>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-900">
                  {t('mercadopago.linkReady')} · {formatMoney(link.amount)}
                </p>
                <Input readOnly value={link.url} onFocus={(event) => event.target.select()} />
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={copyLink}>
                    <Copy size={14} />{copied ? t('mercadopago.copied') : t('mercadopago.copy')}
                  </Button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(t('mercadopago.whatsappText', { url: link.url }))}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="secondary"><MessageCircle size={14} />{t('mercadopago.whatsapp')}</Button>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(paying)}
        title={t('payments.registerPayment')}
        onClose={() => setPaying(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaying(null)}>{t('common.cancel')}</Button>
            <Button onClick={submitPayment} loading={busyId === 'payment'}>{t('common.save')}</Button>
          </>
        }
      >
        {paying && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{paying.first_name} {paying.last_name}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('payments.amount')} required>
                <Input type="number" min="0" value={payment.amount}
                  onChange={(event) => setPayment({ ...payment, amount: event.target.value })} />
              </Field>
              <Field label={t('payments.paidAt')}>
                <Input type="date" value={payment.paidAt}
                  onChange={(event) => setPayment({ ...payment, paidAt: event.target.value })} />
              </Field>
              <Field label={t('payments.method')}>
                <Select value={payment.method}
                  onChange={(event) => setPayment({ ...payment, method: event.target.value })}>
                  {['cash', 'transfer', 'card', 'other'].map((method) => (
                    <option key={method} value={method}>{t(`payments.methods.${method}`)}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <Alert tone="info">{t('payments.applyExplanation')}</Alert>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PaymentsView;
